import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  TrackByFunction
} from "@angular/core";
import { Store } from "@ngxs/store";
import { Tag } from "@vp/models";
import { SharedConfirmationService } from "@vp/shared/confirmation";
import { filterNullMap } from "@vp/shared/operators";
import * as TagManagerActions from "@vp/tag-manager/data-access/tag-manager-state";
import { TagManagerState } from "@vp/tag-manager/data-access/tag-manager-state";
import { Operation } from "rfc6902";
import { Observable, of, Subject } from "rxjs";
import { switchMap, take, takeUntil } from "rxjs/operators";

@Component({
  selector: "vp-tag-menu-selector",
  templateUrl: "./tag-menu-selector.component.html",
  styleUrls: ["./tag-menu-selector.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TagMenuSelectorComponent implements OnDestroy {
  private readonly store = inject(Store);
  private readonly confirmationDialog = inject(SharedConfirmationService);

  selectedTags$: Observable<Tag[]> = inject(Store).select(TagManagerState.selectedTags);
  selectableTags$: Observable<Tag[]> = inject(Store).select(TagManagerState.selectableTags);
  activatedTag$: Observable<Tag | null> = inject(Store).select(TagManagerState.selectedTag);
  pendingOperations$: Observable<Operation[]> = inject(Store).select(TagManagerState.operations);

  private readonly destroyed$ = new Subject<void>();

  editingTag$: Observable<Tag | null> = this.activatedTag$.pipe(
    filterNullMap(),
    takeUntil(this.destroyed$)
  );

  _handlePendingOperations$ = this.pendingOperations$.pipe(
    take(1),
    switchMap(operations => {
      if (operations.length > 0) {
        return this.confirmationDialog
          .open(
            "You have unsaved changes. They will be lost if you continue.",
            "Continue",
            "Pending Changes",
            "tagManagerPendingChanges",
            "30vw",
            true,
            true,
            "warn"
          )
          .afterClosed();
      }
      return of(true);
    })
  );

  addSelected = (tag: Tag) => {
    this.clearState();
    this._handlePendingOperations$.subscribe(proceed => {
      if (!proceed) return;
      this.store.dispatch(new TagManagerActions.SelectTag(tag.tagId));
    });
  };

  removeSelected = (tag: Tag) => {
    this.clearState();
    this._handlePendingOperations$.subscribe(proceed => {
      if (!proceed) return;
      this.store.dispatch(new TagManagerActions.UnselectTag(tag.tagId));
    });
  };

  clearState() {
    this.store.dispatch(
      new TagManagerActions.UpdateState({
        tagSearch: ""
      })
    );
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  trackTagById: TrackByFunction<Tag> = (_: number, item: Tag) => item.tagId;
}
